import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ITableCell, ITableConfig, ITableHeader, ITableRow, ITimelineItem } from '@tecalisdev/infinity';
import { buildIdentityCell } from '../../core/utils/build-identity-cell.util';
import { formatDate, formatFilterTable, notAvailableLabel, truncateUUID } from '../../core/utils/utils';
import {
	IProcessListBody,
	IProcessListOperation,
	IProcessListPayload,
	PROCESS_STATUS,
} from '../../processes/models/processes.models';
import { ProcessHelperService } from '../../processes/services/process-helper.service';
import { MasterService } from '../../shared/services/master.service';
import { IPendingValidationProcessesBody } from '../models/panel.models';

/** Estados de proceso usados en las tablas densas del panel. */
export type PanelProcessListStatus = typeof PROCESS_STATUS.INPROGRESS | typeof PROCESS_STATUS.PENDING;

/** Fila de panel (En curso / Pendiente): mismos campos opcionales que el listado cuando la API los envía. */
type PanelProcessRow = Pick<
	IProcessListBody,
	| 'name'
	| 'document_number'
	| 'document_type'
	| 'process_uuid'
	| 'nationality'
	| 'company'
	| 'status'
	| 'created_at'
	| 'updated_at'
> & {
	operations?: IProcessListOperation[] | null;
};

@Injectable({
	providedIn: 'root',
})
export class PanelProcessesHelperService {
	trans = inject(TranslateService);
	masterService = inject(MasterService);
	private processHelper = inject(ProcessHelperService);

	/** Cabeceras solo para skeleton loading (tablas panel sin `[headers]`, Figma header:false). */
	get header(): ITableHeader[] {
		return [
			{ label: this.trans.instant('TABLE.HEADER.PROCESS_ID') },
			{ label: this.trans.instant('TABLE.HEADER.NAME') },
			{ label: this.trans.instant('TABLE.HEADER.DOCUMENT_NUMBER') },
			{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
			{ label: this.trans.instant('TABLE.HEADER.OPERATIONS') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{ label: this.trans.instant('TABLE.HEADER.DATE_LAST_UPDATE'), align: 'right' },
		];
	}

	public getConfig(type: 'in-progress' | 'pending' = 'in-progress'): ITableConfig {
		let title = this.trans.instant('PANEL.EMPTY_STATES.IN_PROGRESS.TITLE');
		let description = this.trans.instant('PANEL.EMPTY_STATES.IN_PROGRESS.DESCRIPTION');

		if (type === 'pending') {
			title = this.trans.instant('PANEL.EMPTY_STATES.PENDING.TITLE');
			description = this.trans.instant('PANEL.EMPTY_STATES.PENDING.DESCRIPTION');
		}

		return {
			clickeable: true,
			borderless: true,
			status: { type: 'empty', title, description },
		};
	}

	/**
	 * Payload común En curso / Pendiente: mismos filtros globales + per_page 5 + state_uuid fijo.
	 * El estado se reaplica siempre (evita race effect vs ngOnInit y listas idénticas sin filtro).
	 */
	public buildListPayload(
		processStatus: PanelProcessListStatus,
		globalFilters?: Record<string, unknown> | null,
	): IProcessListPayload {
		const statusOption = this.masterService.allStatus?.find((s) => s.value === processStatus);
		const dates = formatFilterTable(globalFilters ?? {});
		delete dates.state_uuid;
		delete dates.per_page;
		delete dates.page;

		return {
			...dates,
			per_page: 5,
			...(statusOption?.id ? { state_uuid: statusOption.id } : {}),
		};
	}

	public transformRow(response: IProcessListBody[] | IPendingValidationProcessesBody[]): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row));
	}

	/**
	 * Orden Figma PANEL: uuid | nombre | id identidad | empresa | timeline | estado | fecha.
	 * Sin copy, sin ojo, sin nacionalidad aparte. Misma fila para En curso y Pendiente.
	 */
	public transformListTableRow(row: PanelProcessRow | IPendingValidationProcessesBody): ITableRow {
		const processUuid = row.process_uuid ?? '';
		const timeline: ITimelineItem[] = this.processHelper.timelineOperations(row.operations);
		const dateValue = row.updated_at || row.created_at;
		const status = row.status;
		const empty = notAvailableLabel(this.trans);

		const cells: ITableCell[] = [
			{ label: processUuid ? truncateUUID(processUuid) : empty },
			{ label: row?.name?.trim() ? row.name.trim() : empty },
			buildIdentityCell(row, this.trans, (type) => this.masterService.getDocumentTypeLabel(type)),
			{ label: row.company?.trim() ? row.company.trim() : empty },
			{
				label: timeline.length > 0 ? '' : empty,
				timeline: timeline.length > 0 ? timeline : undefined,
			},
			status
				? {
						label: '',
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${status}`),
							status: this.masterService.getTagStatus(status),
						},
					}
				: { label: empty },
			{ label: dateValue ? formatDate(dateValue) : empty, align: 'right' },
		];

		return {
			clickValue: processUuid,
			cells,
		};
	}

	public truncateUUID(uuid: string): string {
		return truncateUUID(uuid);
	}
}
